import { useState, createContext, useContext, useEffect } from 'react';
import { 
  useLocation, 
  useNavigate, 
  useParams,
  useSearchParams,
} from 'react-router-dom';

// Create contexts to store router values
const RouterContext = createContext({
  location: { pathname: '/preview', search: '', hash: '', state: null },
  navigate: () => console.log('Navigation attempted in preview mode'),
  params: {},
  isRouterAvailable: false
});

/**
 * A hook that safely provides router functionality regardless of whether
 * the component is inside a Router or not
 */
export const useRouterContext = () => {
  return useContext(RouterContext);
};

// Internal component that actually uses the router hooks
// This is only rendered inside a router
const RouterBridge = ({ children }) => {
  // Always call hooks at the top level
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Provide all router values through context
  const contextValue = {
    location,
    navigate,
    params,
    searchParams,
    setSearchParams,
    isRouterAvailable: true
  };
  
  return (
    <RouterContext.Provider value={contextValue}>
      {children}
    </RouterContext.Provider>
  );
};

// Fallback provider when no router is available
const FallbackRouterProvider = ({ children }) => {
  // Default values when not in a router
  const defaultLocation = { pathname: '/preview', search: '', hash: '', state: null };
  const defaultNavigate = (to) => console.log('Navigation attempted in preview mode:', to);
  const defaultSearchParams = new URLSearchParams();
  
  const contextValue = {
    location: defaultLocation,
    navigate: defaultNavigate,
    params: {},
    searchParams: defaultSearchParams,
    setSearchParams: () => console.log('setSearchParams called in preview mode'),
    isRouterAvailable: false
  };
  
  return (
    <RouterContext.Provider value={contextValue}>
      {children}
    </RouterContext.Provider>
  );
};

/**
 * Router Context Provider that detects if it's in a router and uses the appropriate provider
 */
export const RouterContextProvider = ({ children }) => {
  // Since this is always used inside a Router (from App.js), we can safely use RouterBridge
  // The RouterBridge component will handle the router hooks
  return <RouterBridge>{children}</RouterBridge>;
};

export default useRouterContext; 